import { toast } from 'svelte-sonner';
import { getTypedI18n } from '$lib/types/i18n';
import { getContext } from 'svelte';

// Global state for recovery
let isRecovering = false;
let recoveryAttempts = 0;
const MAX_RECOVERY_ATTEMPTS = 3;

/**
 * Checks if the current version matches the stored version and triggers recovery if needed
 */
const checkVersionAndRecover = async () => {
    if (typeof window === 'undefined') return;

    try {
        // Get the current build version from the response headers
        const currentVersion = document.querySelector('meta[name="build-version"]')?.getAttribute('content') ||
                              document.querySelector('meta[name="x-build-version"]')?.getAttribute('content');

        if (!currentVersion) {
            console.warn('[Cache Invalidation] No build version found in meta tags');
            return;
        }

        // Get the stored version
        const storedVersion = localStorage.getItem('webui-build-version') || null;

        // If versions don't match, trigger recovery
        if (storedVersion !== currentVersion) {
            console.log(`[Cache Invalidation] Version mismatch detected: stored=${storedVersion}, current=${currentVersion}`);

            if (!isRecovering && recoveryAttempts < MAX_RECOVERY_ATTEMPTS) {
                await triggerStaleClientRecovery();
            }
        } else {
            // Update stored version to current
            localStorage.setItem('webui-build-version', currentVersion);
            console.log('[Cache Invalidation] Version check passed:', currentVersion);
        }
    } catch (error) {
        console.warn('[Cache Invalidation] Version check failed:', error);
    }
};

/**
 * Triggers stale client recovery by clearing caches and reloading
 */
const triggerStaleClientRecovery = async () => {
    if (isRecovering || recoveryAttempts >= MAX_RECOVERY_ATTEMPTS) {
        console.warn('[Cache Invalidation] Recovery already in progress or max attempts reached');
        return;
    }

    isRecovering = true;
    recoveryAttempts++;

    console.log(`[Cache Invalidation] Starting stale client recovery (attempt ${recoveryAttempts}/${MAX_RECOVERY_ATTEMPTS})`);

    try {
        // Clear all caches
        await clearAllCaches();

        // Reload with cache busting
        const timestamp = Date.now();
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set('_recovery', timestamp.toString());

        console.log('[Cache Invalidation] Reloading page with cache busting');
        window.location.href = currentUrl.toString();

    } catch (error) {
        console.error('[Cache Invalidation] Recovery failed:', error);
        isRecovering = false;

        // Show user notification
        toast.error('Failed to recover from stale client state. Please refresh manually.');
    }
};

/**
 * Clears all browser caches
 */
const clearAllCaches = async () => {
    console.log('[Cache Invalidation] Clearing all caches...');

    // Clear service worker caches
    if ('caches' in window) {
        try {
            const cacheNames = await caches.keys();
            await Promise.all(
                cacheNames.map(cacheName => caches.delete(cacheName))
            );
            console.log('[Cache Invalidation] Service worker caches cleared');
        } catch (error) {
            console.warn('[Cache Invalidation] Failed to clear service worker caches:', error);
        }
    }

    // Clear IndexedDB
    if ('indexedDB' in window) {
        try {
            const databases = await window.indexedDB.databases();
            await Promise.all(
                databases.map(db => {
                    return new Promise<void>((resolve) => {
                        const request = window.indexedDB.deleteDatabase(db.name || '');
                        request.onsuccess = () => resolve();
                        request.onerror = () => resolve();
                    });
                })
            );
            console.log('[Cache Invalidation] IndexedDB cleared');
        } catch (error) {
            console.warn('[Cache Invalidation] Failed to clear IndexedDB:', error);
        }
    }

    // Clear localStorage (except for critical settings)
    try {
        const criticalKeys = ['theme', 'locale', 'settings'];
        const keysToRemove = Object.keys(localStorage).filter(key =>
            !criticalKeys.includes(key)
        );
        keysToRemove.forEach(key => localStorage.removeItem(key));
        console.log('[Cache Invalidation] localStorage cleared');
    } catch (error) {
        console.warn('[Cache Invalidation] Failed to clear localStorage:', error);
    }

    // Clear sessionStorage
    try {
        sessionStorage.clear();
        console.log('[Cache Invalidation] sessionStorage cleared');
    } catch (error) {
        console.warn('[Cache Invalidation] Failed to clear sessionStorage:', error);
    }
};

/**
 * Sets up the complete cache invalidation system
 */
export const setupCacheInvalidationSystem = () => {
    if (typeof window === 'undefined') return;

    console.log('[Cache Invalidation] Setting up cache invalidation system');

    // Check for recovery parameter on page load
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('_recovery')) {
        console.log('[Cache Invalidation] Page loaded after recovery, removing recovery parameter');
        urlParams.delete('_recovery');
        const newUrl = window.location.pathname + (urlParams.toString() ? `?${urlParams.toString()}` : '') + window.location.hash;
        window.history.replaceState({}, '', newUrl);
    }

    // Reset recovery state on successful page load
    isRecovering = false;
    recoveryAttempts = 0;

    // Check version after a short delay to ensure DOM is ready
    setTimeout(() => {
        checkVersionAndRecover();
    }, 100);

    console.log('[Cache Invalidation] Cache invalidation system ready');
};

/**
 * Manual trigger for stale client recovery (for testing or manual use)
 */
export const manualStaleClientRecovery = () => {
    console.log('[Cache Invalidation] Manual recovery triggered');
    triggerStaleClientRecovery();
};

// Legacy function for backward compatibility
export const handleFrontendAsset404 = () => {
    console.warn('[Cache Invalidation] handleFrontendAsset404 is deprecated, use manualStaleClientRecovery instead');
    manualStaleClientRecovery();
};
